import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  CheckCircleIcon, 
  EyeIcon, 
  ShieldCheckIcon, 
  HeartIcon, 
  RocketLaunchIcon,
  EnvelopeIcon 
} from '@heroicons/react/24/outline';

const valueItems = [
  {
    icon: EyeIcon,
    title: "Visibility",
    description: "A premium spot in our directory so families can find you."
  },
  {
    icon: ShieldCheckIcon,
    title: "Trust",
    description: "The right to use the SeniorThrive brand to show you stand for safety."
  },
  {
    icon: HeartIcon,
    title: "Impact",
    description: "Join the platform making aging at home possible."
  },
  {
    icon: RocketLaunchIcon,
    title: "Growth",
    description: "Exclusive access to upcoming benefits designed to grow your practice."
  }
];

const ProfessionalNetworkSuccess = () => {
  return (
    <>
      <SEO
        title="Application Received"
        description="Your SeniorThrive Professional Network application has been received."
        noindex
      />
      
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex flex-col">
        {/* Header - Logo Only */}
        <header className="py-6 px-4">
          <div className="flex justify-center">
            <Link to="/">
              <img 
                src="/uploads/hero-professional-success-wc.jpg" 
                alt="SeniorThrive" 
                className="h-10 md:h-12"
              />
            </Link>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-3xl">
            <Card className="modern-card bg-gradient-to-r from-card to-card/80 border-primary/20 shadow-overlay">
              <CardContent className="p-8 md:p-12">
                {/* Success Icon */}
                <div className="flex justify-center mb-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-primary to-primary-hover rounded-md flex items-center justify-center shadow-lg">
                    <CheckCircleIcon className="h-10 w-10 text-white" />
                  </div>
                </div>

                {/* Headline */}
                <h1 className="text-3xl md:text-4xl font-bold text-foreground text-center mb-4 leading-tight">
                  Application Received. <span className="text-gradient">Let's Get to Work.</span>
                </h1>

                {/* Subheadline */}
                <p className="text-lg md:text-xl text-muted-foreground text-center mb-10 max-w-2xl mx-auto leading-relaxed">
                  We are reviewing your details now. You are taking the first step toward connecting 
                  your expertise with the families who need it most.
                </p>

                {/* Value Stack Section */}
                <div className="mb-10">
                  <div className="inline-flex items-center gap-2 px-4 py-2 text-primary rounded-full font-medium mb-6 mx-auto block w-fit">
                    <span>What You Just Unlocked</span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {valueItems.map((item, index) => {
                      const IconComponent = item.icon;
                      return (
                        <div 
                          key={index} 
                          className="flex items-start gap-4 p-4 rounded-xl bg-muted/50 hover:transition-colors"
                        >
                          <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center">
                            <IconComponent className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-bold text-foreground mb-1">{item.title}</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Next Step Footer */}
                <div className="rounded-xl p-6 mb-8 border border-primary/10">
                  <div className="flex items-center justify-center gap-3 text-primary">
                    <EnvelopeIcon className="h-6 w-6 flex-shrink-0" />
                    <span className="font-medium text-center">
                      Look for an email from our Partner Success team within 24 hours.
                    </span>
                  </div>
                </div>

                {/* Return Button */}
                <div className="text-center">
                  <Button asChild size="lg" variant="outline" className="px-8">
                    <Link to="/">Return to Home</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-6 px-4 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} SeniorThrive. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <Link to="/privacy" className="hover:text-foreground hover:underline">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground hover:underline">Terms</Link>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ProfessionalNetworkSuccess;
